import { isValidThumbnail } from '@/lib/thumbnail';
import { isOriginalPost } from '@/lib/post-origin';

/**
 * T-249: the single source of truth for "what image, if any, represents a post".
 *
 * WHAT THIS REPLACES. Three hard-coded pools of Unsplash URLs indexed by
 * `post.id.charCodeAt(0)`: four at 1200x600 in PostDetail, the same four at
 * 400x300 in PostCard, a category-split eight at 600x400 in PostCardGrid. They
 * were never a bug report about a broken image path — no editorial image path
 * was ever built at all (boot/publish-articles.ts never sets thumbnail_url), so
 * a graceful default stood in for a decision nobody made, and a Cybercab story
 * and a Sea-Doo jet ski story ended up carrying the identical photograph.
 *
 * WHY A HASHED STOCK PHOTO IS NOT AN ACCEPTABLE DEFAULT. It is not a taste
 * problem. One of the four URLs carries a real IPTC block — 2018 Harley-Davidson
 * 115th Anniversary Celebration, Ben Campbell, (c) Harley-Davidson Motor
 * Company — and it was serving as the hero on articles with no Harley content
 * in them. A photograph asserts "this is what the story is about". When the
 * assertion is false we are publishing a false claim about a named
 * manufacturer's copyrighted image, on the pages we show strangers.
 *
 * THE TWO HONEST ANSWERS, AND WHERE EACH BELONGS:
 *
 *  - `titleCard: true` (the ARTICLE HERO only). For our own editorial we render
 *    the OG title card, /api/og-image/post/:id — the same generator that already
 *    backs every social share. It costs no per-article work, raises no
 *    licensing question, and never claims to depict something it does not.
 *
 *  - no image at all (EVERY FEED CARD, and any post without a usable
 *    thumbnail). Callers get `null` and render the icon treatment that
 *    routes/post.$id.tsx has already shipped for related posts. The title card
 *    is deliberately NOT offered here: on a card the headline is printed
 *    directly beside the thumbnail, so a card that renders the headline into
 *    pixels shows the reader the same sentence twice.
 *
 * A REAL thumbnail always wins over both — the RSS poller does populate it for
 * syndicated posts, and a front-matter `image:` override lands in the same
 * column. It is filtered through T-179's `isValidThumbnail`, which was
 * previously applied in PostCardGrid ONLY; the emoji sprites and tracking
 * pixels it strips were reaching PostCard and PostDetail unchecked.
 */

export interface HeroPost {
  id: string;
  title?: string | null;
  url?: string | null;
  thumbnailUrl?: string | null;
}

export interface PostHeroImage {
  src: string;
  /**
   * Empty for a photograph we did not author and cannot describe — an
   * incorrect alt is worse than a decorative one. The article title for a
   * title card, because there the alt text is exactly, literally, what the
   * pixels say.
   */
  alt: string;
}

/** The OG card endpoint for a post. Relative on purpose: same origin, one container. */
export function ogCardUrl(postId: string): string {
  return `/api/og-image/post/${encodeURIComponent(postId)}`;
}

/**
 * The image to render for a post, or `null` when the honest answer is "we have
 * no image" and the caller must render its no-image treatment.
 *
 * @param titleCard opt in to the OG title card for MotoWalla originals. Only
 *   the article hero should pass this; see the note above.
 */
export function resolvePostHero(
  post: HeroPost,
  { titleCard = false }: { titleCard?: boolean } = {},
): PostHeroImage | null {
  if (isValidThumbnail(post.thumbnailUrl)) {
    return { src: post.thumbnailUrl!, alt: '' };
  }
  if (titleCard && isOriginalPost(post)) {
    return { src: ogCardUrl(post.id), alt: post.title?.trim() || '' };
  }
  return null;
}
